// Public catalog preview route for /orderRequests/$key
// Used by the brand admin iframe preview in aspirex's CatalogDetails page.
// The $key is a base64-encoded JWT generated by PFA's getCatalogPreviewIframeUrl resolver.
// No user session required — the JWT key is the auth credential for PFA calls.
//
// This page mirrors the old member-web-app's ProductCatalogHomePage layout:
// simple header with "Products" title + product count, search bar, product grid,
// and a product detail modal when a product is clicked.
// Preview mode is read-only — no cart, no conditions, no add-to-cart.

import {
  ProductCategoryFilter,
  ProductDetailView,
  ProductGrid,
  type ProductCardData,
} from "@/components/products";
import {
  formatPrice,
  useCatalogByKeyQuery,
  useCollectionsByKeyQuery,
  useFeaturedProductsByKeyQuery,
  useProductDetailByKeyQuery,
  useProductsByKeyInfiniteQuery,
} from "@/lib/queries/products";
import {
  extractFilterOptions,
  filterBySearchQuery,
  filterBySelectedOptions,
} from "@/lib/utils/product-filters";
import { Button, Input, Skeleton } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Search, SlidersHorizontal, Star, X } from "lucide-react";
import { useCallback, useMemo, useState, type ChangeEvent } from "react";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { z } from "zod";

export const Route = createFileRoute("/orderRequests/$key")({
  validateSearch: z.object({
    isPreview: z.coerce.string().optional(),
    // Passed by PFA in the iframe URL — kept in schema so TanStack Router preserves the param
    isApplicationPage: z.coerce.string().optional(),
    brandCatalogId: z.coerce.number().optional(),
    resourceId: z.coerce.number().optional(),
    programId: z.coerce.number().optional(),
    selectionRuleId: z.coerce.number().optional(),
  }),
  component: CatalogPreviewPage,
});

// Shared price computation: prefer metadata prices, fall back to variant prices (EPD-5977).
// Extracted to keep the `products` and `featuredProductsRaw` memos in sync.
function computeProductPrices(product: {
  metadata?: {
    priceMin?: number | string | null;
    priceMax?: number | string | null;
  } | null;
  productVariants?: { price?: number | null }[] | null;
}): { priceMin: number; priceMax: number } {
  let priceMin = Number(product.metadata?.priceMin ?? 0);
  let priceMax = Number(product.metadata?.priceMax ?? 0);
  if (priceMin === 0 && priceMax === 0 && product.productVariants?.length) {
    const prices = product.productVariants
      .map((v) => v.price)
      .filter((px): px is number => px != null && px > 0);
    if (prices.length > 0) {
      priceMin = Math.min(...prices);
      priceMax = Math.max(...prices);
    }
  }
  return { priceMin, priceMax };
}

// Schema for runtime validation of product detail API response
const ProductDetailPayloadSchema = z.object({
  product: z.object({
    id: z.number().catch(0),
    name: z.string().catch(""),
    description: z.string().nullable().catch(null),
    descriptionHtml: z.string().nullable().catch(null),
    images: z.array(z.string()).catch([]),
    productVariants: z
      .array(
        z.object({
          id: z.number(),
          externalId: z.string().nullable().optional(),
          name: z.string().nullable().optional(),
          price: z.union([z.string(), z.number()]).nullable().optional(),
          sku: z.string().nullable().optional(),
          images: z.array(z.string()).nullable().optional(),
          optionType1: z.string().nullable().optional(),
          optionValue1: z.string().nullable().optional(),
          optionType2: z.string().nullable().optional(),
          optionValue2: z.string().nullable().optional(),
          optionType3: z.string().nullable().optional(),
          optionValue3: z.string().nullable().optional(),
          optionType4: z.string().nullable().optional(),
          optionValue4: z.string().nullable().optional(),
          inventory: z
            .object({
              inventoryQuantity: z.number().nullable().optional(),
            })
            .nullable()
            .optional(),
          sellWhenOutOfStock: z.boolean().optional(),
        }),
      )
      .catch([]),
    totalVariants: z.number().catch(0),
  }),
});

function CatalogPreviewPage() {
  const { key } = Route.useParams();
  const search = Route.useSearch();
  const catalogId = search.brandCatalogId ?? 0;
  const resourceId = search.resourceId;

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    () => new Set(),
  );
  const [pendingCategories, setPendingCategories] = useState<Set<string>>(
    () => new Set(),
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [selectedProductBrandCatalogId, setSelectedProductBrandCatalogId] =
    useState<number | undefined>(undefined);

  // ---------- Data fetching ----------

  const {
    data: catalog,
    isPending: isCatalogPending,
    error: catalogError,
  } = useCatalogByKeyQuery(key, catalogId);

  const { data: collectionsData, error: collectionsError } =
    useCollectionsByKeyQuery(key, catalogId);

  const {
    data: productsData,
    isPending: isProductsPending,
    error: productsError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useProductsByKeyInfiniteQuery(
    key,
    catalogId,
    debouncedSearchQuery,
    20,
    resourceId,
  );

  // Fetch product detail when a product is selected
  const {
    data: productDetail,
    isPending: isProductDetailPending,
    error: productDetailError,
  } = useProductDetailByKeyQuery(
    key,
    selectedProductId ?? 0,
    selectedProductBrandCatalogId,
  );

  // ---------- Derived data ----------

  // Catalog settings
  const catalogSettings = catalog?.settings as
    | {
        isFeatureProductsEnable?: boolean | null;
        hidePriceFromCreators?: boolean | null;
        hideTagsFromCreators?: boolean | null;
        hideVendorsFromCreators?: boolean | null;
      }
    | null
    | undefined;
  const showPrice = isCatalogPending
    ? false
    : !catalogSettings?.hidePriceFromCreators;
  const showVendor = !catalogSettings?.hideVendorsFromCreators;
  const showTags = !catalogSettings?.hideTagsFromCreators;
  const featureProductsEnabled = !!catalogSettings?.isFeatureProductsEnable;

  // Build collection lookup maps
  const collectionsByProductId = useMemo(() => {
    const map = new Map<number, string[]>();
    if (!collectionsData) return map;
    for (const collection of collectionsData) {
      const productIds = collection.productIds ?? [];
      for (const pid of productIds) {
        const existing = map.get(pid) ?? [];
        if (collection.collectionName) {
          existing.push(collection.collectionName);
        }
        map.set(pid, existing);
      }
    }
    return map;
  }, [collectionsData]);

  // Map productId → underlying collection.externalId values. EPD-8597 — canonical
  // match key against condition.collections[].id (Shopify GID or CSV slug).
  const collectionExternalIdsByProductId = useMemo(() => {
    const map = new Map<number, string[]>();
    if (!collectionsData) return map;
    for (const collection of collectionsData) {
      if (!collection.externalId) continue;
      for (const pid of collection.productIds ?? []) {
        const existing = map.get(pid) ?? [];
        existing.push(collection.externalId);
        map.set(pid, existing);
      }
    }
    return map;
  }, [collectionsData]);

  // All collection names (for filter options)
  const allCollectionNames = useMemo(() => {
    if (!collectionsData) return [];
    const names = new Set<string>();
    for (const c of collectionsData) {
      if (c.collectionName) names.add(c.collectionName);
    }
    return Array.from(names);
  }, [collectionsData]);

  const products = useMemo<ProductCardData[]>(() => {
    if (!productsData?.pages) return [];
    const result: ProductCardData[] = [];
    for (const page of productsData.pages) {
      for (const p of page?.products ?? []) {
        if (p.product == null) continue;
        const { priceMin, priceMax } = computeProductPrices(p.product);
        const productId = p.product.id ?? 0;
        result.push({
          id: productId,
          catalogProductId: p.id,
          brandCatalogId: p.brandCatalogId,
          name: p.product.name ?? "",
          image: p.product.images?.[0] ?? null,
          priceMin,
          priceMax,
          currencyCode: p.product.metadata?.currencyCode ?? "USD",
          category: p.product.category ?? null,
          productType: p.product.productType ?? null,
          collectionNames: collectionsByProductId.get(productId),
          collectionExternalIds:
            collectionExternalIdsByProductId.get(productId),
          vendor: (p.product as { vendor?: string | null }).vendor ?? null,
          tags: (p.product as { tags?: string[] | null }).tags ?? null,
          isFeatured: (p as { isFeatured?: boolean }).isFeatured ?? false,
        });
      }
    }
    return result;
  }, [productsData, collectionsByProductId, collectionExternalIdsByProductId]);

  // Client-side search + category filtering
  const filteredProducts = useMemo(() => {
    const searched = filterBySearchQuery(products, searchQuery);
    return selectedCategories.size === 0
      ? searched
      : filterBySelectedOptions(searched, selectedCategories);
  }, [products, searchQuery, selectedCategories]);

  // Featured products — fetched server-side via isFeatured:true filter (EPD-6697)
  const featuredProductsQuery = useFeaturedProductsByKeyQuery(
    key,
    catalogId,
    resourceId,
    featureProductsEnabled,
  );

  const featuredProductsRaw = useMemo<ProductCardData[]>(() => {
    const raw = featuredProductsQuery.data?.products ?? [];
    const result: ProductCardData[] = [];
    for (const p of raw) {
      if (p.product == null) continue;
      const productId = p.product.id ?? 0;
      const { priceMin, priceMax } = computeProductPrices(p.product);
      result.push({
        id: productId,
        catalogProductId: p.id,
        brandCatalogId: p.brandCatalogId,
        name: p.product.name ?? "",
        image: p.product.images?.[0] ?? null,
        priceMin,
        priceMax,
        currencyCode: p.product.metadata?.currencyCode ?? "USD",
        category: p.product.category ?? null,
        productType: p.product.productType ?? null,
        collectionNames: collectionsByProductId.get(productId),
        collectionExternalIds: collectionExternalIdsByProductId.get(productId),
        vendor: (p.product as { vendor?: string | null }).vendor ?? null,
        tags: (p.product as { tags?: string[] | null }).tags ?? null,
        isFeatured: true,
      });
    }
    return result;
  }, [
    featuredProductsQuery.data,
    collectionsByProductId,
    collectionExternalIdsByProductId,
  ]);

  // Available filter categories — include options from the featured strip so
  // featured-only categories appear as filter choices (EPD-6697).
  const availableCategories = useMemo(() => {
    const merged = new Set(extractFilterOptions(products));
    for (const opt of extractFilterOptions(featuredProductsRaw)) {
      merged.add(opt);
    }
    for (const name of allCollectionNames) {
      merged.add(name);
    }
    return Array.from(merged).sort();
  }, [products, featuredProductsRaw, allCollectionNames]);

  // Run featured products through the same search + category filter pipeline as the main grid
  const featuredProducts = useMemo<ProductCardData[]>(() => {
    const searched = filterBySearchQuery(featuredProductsRaw, searchQuery);
    return selectedCategories.size === 0
      ? searched
      : filterBySelectedOptions(searched, selectedCategories);
  }, [featuredProductsRaw, searchQuery, selectedCategories]);

  const showFeaturedProducts =
    featureProductsEnabled && featuredProducts.length > 0;

  const activeFilterCount = selectedCategories.size;

  // Currently selected product from the grid or featured strip (for detail view).
  // Featured products may not be in the paginated `products` list if they were
  // not yet loaded, so fall back to featuredProductsRaw to preserve currencyCode.
  const selectedProduct = useMemo(
    () =>
      selectedProductId != null
        ? (products.find((p) => p.catalogProductId === selectedProductId) ??
          featuredProductsRaw.find(
            (p) => p.catalogProductId === selectedProductId,
          ))
        : undefined,
    [products, featuredProductsRaw, selectedProductId],
  );

  // Normalized product detail for the ProductDetailView
  const normalizedProductDetail = useMemo(() => {
    if (!productDetail) return undefined;

    const parsed = ProductDetailPayloadSchema.safeParse(productDetail);
    if (!parsed.success) {
      console.error(
        "Failed to parse product detail payload:",
        parsed.error.flatten(),
      );
      return undefined;
    }

    return parsed.data;
  }, [productDetail]);

  // ---------- Callbacks ----------

  const handleFilterOpenChange = useCallback(
    (open: boolean) => {
      setIsFilterOpen(open);
      if (open) {
        setPendingCategories(
          new Set(
            [...selectedCategories].filter((c) =>
              availableCategories.includes(c),
            ),
          ),
        );
      }
    },
    [selectedCategories, availableCategories],
  );

  const handleToggleCategory = useCallback((category: string) => {
    setPendingCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  const handleApplyFilters = useCallback(() => {
    setSelectedCategories(new Set(pendingCategories));
    setIsFilterOpen(false);
  }, [pendingCategories]);

  const handleRemoveCategory = useCallback((category: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      next.delete(category);
      return next;
    });
    setPendingCategories((prev) => {
      const next = new Set(prev);
      next.delete(category);
      return next;
    });
  }, []);

  const handleClearFilters = useCallback(() => {
    setPendingCategories(new Set());
    setSelectedCategories(new Set());
  }, []);

  const handleSetSearchQuery = useCallback((q: string) => {
    setSearchQuery(q);
  }, []);

  const handleProductClick = useCallback((product: ProductCardData) => {
    if (product.catalogProductId) {
      setSelectedProductId(product.catalogProductId);
      setSelectedProductBrandCatalogId(product.brandCatalogId);
    }
  }, []);

  const handleCloseProductDetail = useCallback(() => {
    setSelectedProductId(null);
    setSelectedProductBrandCatalogId(undefined);
  }, []);

  // Noop — preview mode, no cart operations
  const noop = useCallback(() => {}, []);

  const isLoading =
    isCatalogPending || (isProductsPending && products.length === 0);

  // ---------- Render ----------

  if (!catalogId) {
    return (
      <div className="flex items-center justify-center h-screen text-muted-foreground text-sm">
        No catalog specified.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="px-6 pb-6 space-y-6">
        <Skeleton className="h-[200px] w-full rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton
            <Skeleton key={i} className="h-[300px] w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (catalogError || productsError || collectionsError) {
    return (
      <div className="flex items-center justify-center h-screen text-muted-foreground text-sm">
        Unable to load catalog preview. Please try again later.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-screen [overflow-x:clip]">
      {/* Header — matches old MWA layout: "Products" title + count */}
      <div className="sticky top-0 z-30 bg-background border-b">
        <div className="px-4 sm:px-6 py-3">
          <h1 className="text-xl font-bold">Products</h1>
          <p className="text-sm text-muted-foreground">
            {filteredProducts.length}{" "}
            {filteredProducts.length === 1 ? "Product" : "Products"}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-4 sm:px-6 w-full min-w-0 space-y-4">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleSetSearchQuery(e.target.value)
              }
              className="pl-9"
            />
          </div>
          {availableCategories.length > 0 && (
            <Button
              variant="outline"
              className="gap-2 shrink-0"
              onClick={() => handleFilterOpenChange(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Selected Categories Tags */}
        {selectedCategories.size > 0 && (
          <div className="flex flex-wrap gap-2">
            {Array.from(selectedCategories).map((category) => (
              <Button
                key={category}
                variant="secondary"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleRemoveCategory(category)}
              >
                {category}
                <X className="ml-1 h-3 w-3" />
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={handleClearFilters}
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Featured Products — horizontal scroll row */}
        {showFeaturedProducts && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
              <h2 className="text-lg font-semibold text-foreground">
                Featured
              </h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {featuredProducts.map((product) => (
                <div
                  key={product.catalogProductId}
                  className="w-48 shrink-0 cursor-pointer"
                  onClick={() => handleProductClick(product)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleProductClick(product);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-2">
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <p className="text-sm font-medium line-clamp-2">
                    {product.name}
                  </p>
                  {showPrice &&
                    (product.priceMin > 0 || product.priceMax > 0) && (
                      <p className="text-sm text-muted-foreground">
                        {product.priceMin === product.priceMax
                          ? `${formatPrice(product.priceMin, product.currencyCode)}`
                          : `${formatPrice(product.priceMin, product.currencyCode)} - ${formatPrice(product.priceMax, product.currencyCode)}`}
                      </p>
                    )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product Grid */}
        <ProductGrid
          products={filteredProducts}
          onProductClick={handleProductClick}
          showPrice={showPrice}
          showVendor={showVendor}
          showTags={showTags}
          hasMore={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      </div>

      {/* Product Detail Modal — opens when a product is clicked */}
      {selectedProductId !== null && (
        <ProductDetailView
          key={selectedProductId}
          product={normalizedProductDetail?.product}
          isLoading={isProductDetailPending}
          error={productDetailError ?? null}
          showPrice={showPrice}
          showVendor={showVendor}
          showTags={showTags}
          currencyCode={selectedProduct?.currencyCode ?? "USD"}
          cartVariantIds={EMPTY_SET}
          onAddToCart={noop}
          onBack={handleCloseProductDetail}
        />
      )}

      {/* Category Filter Dialog / Drawer */}
      <ProductCategoryFilter
        open={isFilterOpen}
        onOpenChange={handleFilterOpenChange}
        categories={availableCategories}
        selectedCategories={pendingCategories}
        onToggleCategory={handleToggleCategory}
        onClear={handleClearFilters}
        onApply={handleApplyFilters}
      />
    </div>
  );
}

// Stable empty set for ProductDetailView cartVariantIds (preview mode — no cart)
const EMPTY_SET = new Set<number>();
